import { atom, selector } from 'recoil'
import {
  IMAGE_QUALITY_BANNER_PPI_THRESHOLD,
  IMAGE_QUALITY_PAPER_PPI_THRESHOLD,
} from '@messenger/consts'

export type ImageQualityInfo = {
  imageId: string
  hasQualityIssue: boolean
  effectivePixelsPerInch: number | null
  threshold: number
}

// Atom to store all image quality information
export const imageQualityAtom = atom<ImageQualityInfo[]>({
  key: 'imageQualityAtom',
  default: [],
})

// Selector to determine if there are any quality issues (used for save-time checks)
export const hasAnyImageQualityIssuesSelector = selector({
  key: 'hasAnyImageQualityIssuesSelector',
  get: ({ get }) => {
    const imageQualityList = get(imageQualityAtom)
    return imageQualityList.some((image) => image.hasQualityIssue)
  },
})

// Selector to get the appropriate threshold (banner vs paper products)
export const imageQualityThresholdSelector = selector({
  key: 'imageQualityThresholdSelector',
  get: ({ get }) => {
    const imageQualityList = get(imageQualityAtom)
    // If we have any banner products (threshold 150), use that, otherwise use paper (200)
    const hasBannerProduct = imageQualityList.some(
      (image) => image.threshold === IMAGE_QUALITY_BANNER_PPI_THRESHOLD,
    )
    return hasBannerProduct
      ? IMAGE_QUALITY_BANNER_PPI_THRESHOLD
      : IMAGE_QUALITY_PAPER_PPI_THRESHOLD
  },
})

// Atom to control the quality modal display (only used during save operations)
export const showImageQualityModalAtom = atom({
  key: 'showImageQualityModalAtom',
  default: false,
})

// Atom to control whether to show acknowledge button (for save-time warnings)
export const showImageQualityAcknowledgeButtonAtom = atom({
  key: 'showImageQualityAcknowledgeButtonAtom',
  default: false,
})

// Atom to track if initial page load image quality check has been completed
export const initialImageQualityCheckCompletedAtom = atom({
  key: 'initialImageQualityCheckCompletedAtom',
  default: false,
})

// Atom to track if we've already shown the page load quality modal (prevent repeated showings)
export const hasShownPageLoadQualityModalAtom = atom({
  key: 'hasShownPageLoadQualityModalAtom',
  default: false,
})
